/**
 * Address Validation Utilities
 *
 * Utility functions for BA check address validation.
 * Handles address comparison, hint extraction, and alert data building.
 */

import type {
  AddressFieldChange,
  AddressCorrectionAlertData,
} from '@/types/schemas/checkout/addressValidation';

/** Translation keys for address field labels */
export const ADDRESS_FIELD_TRANSLATION_KEYS: Record<
  AddressFieldChange['field'],
  { key: string; fallback: string }
> = {
  street: { key: 'ibe.form.label.street', fallback: 'Street' },
  postalCode: { key: 'ibe.form.label.postalCode', fallback: 'Postal Code' },
  city: { key: 'ibe.form.label.location', fallback: 'City' },
  country: { key: 'ibe.form.label.country', fallback: 'Country' },
};

/**
 * Compare sent address with response address and detect changes.
 *
 * Country codes are normalized for case-insensitive comparison because:
 * - Form stores lowercase ISO alpha-2 codes (de, at, fr, ...)
 * - API returns uppercase ISO alpha-2 codes (DE, AT, FR, ...)
 */
export function detectAddressChanges(
  sentAddress: { street?: string; postalCode?: string; city?: string; country?: string },
  responseAddress: { Street?: string; ZipCode?: string; City?: string; Country?: string }
): AddressFieldChange[] {
  const changes: AddressFieldChange[] = [];

  const fieldMappings: Array<{
    field: AddressFieldChange['field'];
    sent: string | undefined;
    response: string | undefined;
  }> = [
    { field: 'street', sent: sentAddress.street, response: responseAddress.Street },
    { field: 'postalCode', sent: sentAddress.postalCode, response: responseAddress.ZipCode },
    { field: 'city', sent: sentAddress.city, response: responseAddress.City },
    { field: 'country', sent: sentAddress.country, response: responseAddress.Country },
  ];

  for (const mapping of fieldMappings) {
    // Case-insensitive comparison for all fields
    const sentNormalized = mapping.sent?.trim().toLowerCase() ?? '';
    const responseNormalized = mapping.response?.trim().toLowerCase() ?? '';

    if (sentNormalized && responseNormalized && sentNormalized !== responseNormalized) {
      changes.push({
        field: mapping.field,
        originalValue: mapping.sent ?? '',
        correctedValue: mapping.response ?? '',
      });
    }
  }

  return changes;
}

/**
 * Extract hints from DaVinci booking response
 */
export function extractHints(booking: { Hints?: Array<{ Info?: string }> }): string[] {
  if (!booking.Hints || !Array.isArray(booking.Hints)) {
    return [];
  }
  return booking.Hints.filter((hint) => hint.Info).map((hint) => hint.Info as string);
}

/**
 * Build structured alert data from detected address changes.
 *
 * Returns structured data with translation keys - translation happens in UI layer.
 * This keeps the utility function pure and testable.
 *
 * Note: API hints are intentionally NOT included. We display our own user-friendly
 * messages based on detected address changes instead of showing raw API hints
 * which may contain irrelevant information (e.g., payment method, request status).
 */
export function buildAddressCorrectionAlertData(params: {
  addressChanges: AddressFieldChange[];
  shouldSwitchToRequestBooking: boolean;
}): AddressCorrectionAlertData {
  return {
    addressChanges: params.addressChanges.map((change) => ({
      field: change.field,
      fieldLabelKey: ADDRESS_FIELD_TRANSLATION_KEYS[change.field].key,
      fieldLabelFallback: ADDRESS_FIELD_TRANSLATION_KEYS[change.field].fallback,
      originalValue: change.originalValue,
      correctedValue: change.correctedValue,
    })),
    showFlowChangedNotice: params.shouldSwitchToRequestBooking,
  };
}
